// CARO. Casa de estilo — Google Analytics (GA4) loader.
// GA is loaded ONLY after the user grants analytics consent. Nothing from
// Google is requested before that (no cookies, no script). Uses Consent Mode.
import { HANDOFF_PARAM } from '@/lib/colorimetry/handoff';
import { DOLL_PARAM } from '@/lib/colorimetry/route';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let loaded = false;

/**
 * /admin (behind Cloudflare Access) is the owner's own traffic: GA never loads or counts there, even
 * with analytics consent stored in that browser. Same test as the Worker's /admin routing.
 */
function isAdminPath(): boolean {
  const path = window.location.pathname;
  return path === '/admin' || path.startsWith('/admin/');
}

export function initGA(measurementId: string): void {
  if (loaded || !measurementId || isAdminPath()) return;
  loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // gtag.js only processes dataLayer entries that are `arguments` objects
  // ([object Arguments]); a plain array (e.g. from rest params) is silently
  // ignored, so js/consent/config/event never run and nothing reaches
  // /collect. Forward the real `arguments` object, like the canonical snippet.
  const gtag: (...args: unknown[]) => void = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag = gtag;

  gtag('js', new Date());
  // Consent Mode: default denied, then granted (we only reach here on consent).
  gtag('consent', 'default', { ad_storage: 'denied', analytics_storage: 'denied' });
  gtag('consent', 'update', { analytics_storage: 'granted' });
  gtag('config', measurementId, { anonymize_ip: true, send_page_view: false });
}

/**
 * The page URL for GA without the COLORIMETRY ME! result (`?ficha=`, lib/colorimetry/handoff) or the doll
 * of «Mi estudio» (`?p=`, lib/studio/doll): they may sit in the address bar, and the site never sends
 * answers, results or what a person says about herself. Other params (utm_*) stay.
 */
function pageLocation(): string {
  const url = new URL(window.location.href);
  for (const p of [HANDOFF_PARAM, DOLL_PARAM]) url.searchParams.delete(p);
  return url.toString();
}

export function gaEvent(event: string, params?: Record<string, unknown>): void {
  if (!loaded || typeof window.gtag !== 'function' || isAdminPath()) return;
  window.gtag('event', event, { page_location: pageLocation(), ...params });
}

/** Best-effort disable if the user withdraws consent after granting it. */
export function disableGA(measurementId: string): void {
  (window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = true;
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', { analytics_storage: 'denied' });
  }
}
